import {
  ChangeEvent,
  FocusEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { differenceInDays, isAfter, isBefore, isValid } from 'date-fns';
import { merge } from 'lodash-es';
import { InteractOutsideEvent } from '@zag-js/popover';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useEventHandler } from '../../../../../hooks/useEventHandler';
import { Nullable } from '../../../../../types';
import { DateTimeDisplayMode } from '../../../../DateTimeDisplay/constants/DateTimeDisplayMode';
import { formatDateTime } from '../../../../DateTimeDisplay/logic/formatDateTime';
import { DEFAULT_AVAILABLE_DAYS_RANGE } from '../../constants/defaultAvailableDaysRange';
import { getAvailableDateRange } from '../../logic/getAvailableDateRange';
import { DatePickerInputProps } from '../../types/DatePickerInputProps';
import { parseDate } from './logic/parseDate';

export const useDatePicker = (
  _ref: DatePickerInputProps['_ref'],
  onChange: DatePickerInputProps['onChange'],
  onDateChange: DatePickerInputProps['onDateChange'],
  value: DatePickerInputProps['value'],
  availableDaysRange: DatePickerInputProps['availableDaysRange'],
) => {
  const mergedAvailableDateRange = useMemo(
    () =>
      getAvailableDateRange(
        merge(DEFAULT_AVAILABLE_DAYS_RANGE, availableDaysRange),
      ),
    [availableDaysRange],
  );
  const selectedDate = useMemo(() => parseDate(value || '', 'value'), [value]);
  const [inputTypeValue, setInputTypeValue] = useState<string>(
    selectedDate ? formatDateTime(selectedDate, DateTimeDisplayMode.Date) : '',
  );
  const datePickerPopoverApi = useOpenClose();
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    selectedDate ?? new Date(),
  );
  const inputContainerRef = useRef<Nullable<HTMLDivElement>>(null);
  const inputRef = useRef<Nullable<HTMLInputElement>>(null);
  const popoverContentRef = useRef<Nullable<HTMLDivElement>>(null);

  useEffect(() => {
    // this is a controlled input, but we internally manage the typed value
    // keep this in sync if/when the selected date changes from outside
    if (!selectedDate) {
      setInputTypeValue('');
    } else if (isValid(selectedDate)) {
      setInputTypeValue(formatDateTime(selectedDate, DateTimeDisplayMode.Date));
    }
  }, [selectedDate]);

  const handleOnBlur = useEventHandler((e: FocusEvent<HTMLDivElement>) => {
    if (!datePickerPopoverApi.open) return;
    const target = (e.relatedTarget as Node) ?? (e.target as Node);
    if (inputContainerRef.current?.contains(target)) {
      return;
    }
    if (popoverContentRef.current?.contains(target)) {
      return;
    }
    datePickerPopoverApi.onClose();
  });

  const handleInteractOutside = useEventHandler(
    (event: InteractOutsideEvent) => {
      if (!datePickerPopoverApi.open) return;
      const target = event.target as Node;
      if (inputContainerRef.current?.contains(target)) {
        return;
      }
      if (popoverContentRef.current?.contains(target)) {
        return;
      }
      datePickerPopoverApi.onClose();
    },
  );

  useEffect(() => {
    if (!datePickerPopoverApi.open) {
      setVisibleMonth(selectedDate ?? new Date());
    }
  }, [datePickerPopoverApi.open, selectedDate]);

  const setDate = useEventHandler((day: Date | undefined) => {
    if (day) {
      const inputTypedValue = formatDateTime(day, DateTimeDisplayMode.Date);
      onChange(formatDateTime(day, DateTimeDisplayMode.DateKey));
      onDateChange?.(formatDateTime(day, DateTimeDisplayMode.DateKey));
      setInputTypeValue(inputTypedValue);
      datePickerPopoverApi.onClose();
    }
  });

  const clearDate = useEventHandler(() => {
    onChange('');
    onDateChange?.('');
  });

  const handleTypeDate = useEventHandler((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    setInputTypeValue(newValue);

    if (!newValue) {
      clearDate();
      return;
    }

    const arrDate = newValue.replace(/\s/g, '').split('/');

    if (arrDate[1] && arrDate[1].length === 2) {
      setVisibleMonth(
        (prev) => new Date(prev.getFullYear(), Number(arrDate[1]) - 1),
      );
    }

    if (arrDate[2] && arrDate[2].length === 4) {
      setVisibleMonth(
        () => new Date(Number(arrDate[2]), Number(arrDate[1]) - 1),
      );
      const date = new Date(arrDate.reverse().join('-'));
      if (isValid(date)) {
        if (
          isAfter(date, mergedAvailableDateRange.after) &&
          isBefore(date, mergedAvailableDateRange.before)
        ) {
          setDate(date);
        } else {
          // normalize date to nearest available date
          const distanceToMinDate = differenceInDays(
            date,
            mergedAvailableDateRange.after,
          );
          const distanceToMaxDate = differenceInDays(
            mergedAvailableDateRange.before,
            date,
          );

          if (distanceToMinDate < distanceToMaxDate) {
            setDate(mergedAvailableDateRange.after);
          } else {
            setDate(mergedAvailableDateRange.before);
          }
        }
      }
    }
  });

  const handleOnFocus = useEventHandler((e: FocusEvent<HTMLInputElement>) => {
    const target = (e.relatedTarget as Node) ?? (e.target as Node);
    if (popoverContentRef.current?.contains(target)) {
      return;
    }
    datePickerPopoverApi.onOpen();
  });

  const handleOnClick = useEventHandler(() => {
    datePickerPopoverApi.onOpen();
  });

  const handleSetRef = useCallback(
    (el: Nullable<HTMLInputElement>) => {
      inputRef.current = el;
      if (typeof _ref === 'function') {
        _ref(el);
      } else if (_ref) {
        // eslint-disable-next-line no-param-reassign
        (_ref as MutableRefObject<Nullable<HTMLInputElement>>).current = el;
      }
    },
    [_ref],
  );

  return {
    mergedAvailableDateRange,
    inputContainerRef,
    inputTypeValue,
    datePickerPopoverApi,
    visibleMonth,
    handleTypeDate,
    setVisibleMonth,
    setDate,
    selectedDate,
    handleOnClick,
    handleOnFocus,
    handleOnBlur,
    popoverContentRef,
    handleSetRef,
    handleInteractOutside,
  };
};
